/**
 * 이미지 생성 오케스트레이션 — 결정 #18/#19를 잇는다.
 *
 *  1) 이미지 게이트웨이로 생성(프로바이더 + 키 해석, 결정 #19).
 *  2) 결과 Blob을 콘텐츠 주소화 에셋으로 저장(결정 #18).
 *  3) GeneratedImage provenance 레코드를 작성 → 갤러리가 즉시 반응형으로 표시
 *     (결정 #13/#19). 이 메타가 있어야 "모든 생성 이미지 몰아보기"가 가능하다.
 *
 * 저장소/게이트웨이/팩토리는 core 인터페이스로만 다룬다(결정 #10).
 */
import {
  createGeneratedImage,
  providerSupportsCapability,
  type GeneratedImage,
  type ImageGenParams,
  type ProviderProfile,
} from "@roqeet/core";
import { storage } from "./storage.js";
import { imageGateway } from "./image.js";
import { importAsset } from "../platform/assets/index.js";

export interface GenerateImageInput extends ImageGenParams {
  /** 생성에 사용할 프로바이더 프로필. 미지정 시 기본 프로필을 쓴다. */
  profileId?: string;
  /** 갤러리 필터링용 컨텍스트(결정 #19). */
  chatId?: string;
  messageId?: string;
  characterId?: string;
}

/** 활성 프로바이더 프로필을 해석한다(이미지도 텍스트 프로필 재사용, 결정 #19). */
async function resolveProfile(profileId?: string): Promise<ProviderProfile> {
  const profiles = (await storage.providerProfiles.list()).filter(
    (p) => p.enabled && providerSupportsCapability(p, "image"),
  );
  const settings = await storage.getSettings();
  const id = profileId ?? settings?.defaultProviderProfileId;
  const chosen = (id && profiles.find((p) => p.id === id)) || profiles[0];
  if (!chosen) {
    throw new Error("사용 가능한 프로바이더가 없습니다. 설정에서 프로바이더를 먼저 추가하세요.");
  }
  return chosen;
}

/**
 * 프롬프트로 이미지를 생성하고 저장한다. 생성된 GeneratedImage 레코드 배열을
 * 반환한다(보통 1개, count>1이면 여러 개).
 */
export async function generateImage(input: GenerateImageInput): Promise<GeneratedImage[]> {
  const { profileId, chatId, messageId, characterId, ...genParams } = input;
  const profile = await resolveProfile(profileId);

  const results = await imageGateway.generate(genParams, profile);
  const records: GeneratedImage[] = [];

  for (const result of results) {
    // 1) Blob → 콘텐츠 주소화 에셋(결정 #18).
    const asset = await importAsset(result.blob);

    // 2) provenance 레코드(결정 #19). 어댑터가 채운 provenance + 컨텍스트 병합.
    const record = createGeneratedImage({
      ...result.provenance,
      assetId: asset.id,
      ...(chatId ? { chatId: chatId as GeneratedImage["chatId"] } : {}),
      ...(messageId ? { messageId: messageId as GeneratedImage["messageId"] } : {}),
      ...(characterId ? { characterId: characterId as GeneratedImage["characterId"] } : {}),
    });
    await storage.generatedImages.put(record);
    records.push(record);
  }

  return records;
}
